import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { CirclePause, Clock, ShoppingBag, ShoppingCart, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { loadHeldCart, setHeldCartCount } from './cartSlice';
import api, { getErrorMessage } from '@/services/api';
import { API_ENDPOINTS } from '@/utils/constants';
import { Alert, Button, Card, IconTile, LoadingSkeleton, Sheet, SummaryLine, initials } from '@/components/ui';
import { PageShell } from '@/components/layout/PageShell';
import { formatDateTime, formatCurrency } from '@/utils/formatters';
import { useIsLandscape } from '@/hooks/useMediaQuery';
import { HeldCartListItem, HoldCartResponse } from '@/services/types';
import { parseOrderType } from '@/utils/orderType';

const timeOf = (iso: string) => new Date(iso).toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit' });
const expiringSoon = (iso: string) => new Date(iso).getTime() - Date.now() < 60 * 60 * 1000;

/** 13 Keranjang ditahan. Phone: cards. Landscape: 440px list + detail with items. */
const HeldCartsPage: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const isLandscape = useIsLandscape();
    const { selectedShop } = useAppSelector((state) => state.auth);

    const [heldCarts, setHeldCarts] = useState<HeldCartListItem[]>([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [selectedCart, setSelectedCart] = useState<HeldCartListItem | null>(null);
    const [detail, setDetail] = useState<HoldCartResponse | null>(null);
    const [cancelTarget, setCancelTarget] = useState<HeldCartListItem | null>(null);
    const [actionLoading, setActionLoading] = useState(false);

    useEffect(() => { if (selectedShop) fetchHeldCarts(); // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedShop]);

    const fetchHeldCarts = async () => {
        if (!selectedShop) return;
        setLoading(true);
        setError(null);
        try {
            const response = await api.get(API_ENDPOINTS.HELD_CARTS(selectedShop.shopId));
            const carts: HeldCartListItem[] = response.data.data.carts || [];
            setHeldCarts(carts);
            setTotal(response.data.data.total || carts.length);
            dispatch(setHeldCartCount(response.data.data.total || 0));
            if (isLandscape && carts[0] && !selectedCart) select(carts[0]);
        } catch (err) {
            const message = getErrorMessage(err);
            setError(message);
            toast.error(message);
        } finally {
            setLoading(false);
        }
    };

    const select = async (cart: HeldCartListItem) => {
        if (!selectedShop) return;
        setSelectedCart(cart);
        setDetail(null);
        try {
            const response = await api.get(API_ENDPOINTS.HELD_CART_BY_ID(selectedShop.shopId, cart.id));
            setDetail(response.data.data);
        } catch (err) {
            toast.error(getErrorMessage(err));
        }
    };

    const handleRetrieveCart = async (cart: HeldCartListItem | null) => {
        if (!selectedShop || !cart) return;
        setActionLoading(true);
        try {
            const response = await api.post(API_ENDPOINTS.RETRIEVE_HELD_CART(selectedShop.shopId, cart.id));
            const retrievedCart = response.data.data;
            const parsed = parseOrderType(retrievedCart.notes);
            fetchHeldCarts();
            dispatch(loadHeldCart({
                items: retrievedCart.items,
                customerName: retrievedCart.customerName,
                customerPhone: retrievedCart.customerPhone,
                notes: parsed.notes,
                orderType: parsed.orderType ?? undefined,
            }));
            toast.success(t('heldCarts.retrievedSuccess'));
            navigate('/cart');
        } catch (err) {
            toast.error(getErrorMessage(err));
        } finally {
            setActionLoading(false);
        }
    };

    const handleCancelCart = async () => {
        if (!selectedShop || !cancelTarget) return;
        setActionLoading(true);
        try {
            await api.delete(API_ENDPOINTS.CANCEL_HELD_CART(selectedShop.shopId, cancelTarget.id));
            toast.success(t('heldCarts.cancelledSuccess'));
            setCancelTarget(null);
            if (selectedCart?.id === cancelTarget.id) { setSelectedCart(null); setDetail(null); }
            fetchHeldCarts();
        } catch (err) {
            toast.error(getErrorMessage(err));
        } finally {
            setActionLoading(false);
        }
    };

    if (!selectedShop) return null;

    /** "Makan di tempat" from the notes tag, else the cashier who held it */
    const typeOrHolder = (cart: HeldCartListItem) => {
        const { orderType } = parseOrderType(cart.notes);
        return orderType ? t(`cart.orderTypes.${orderType}` as 'cart.orderTypes.DINE_IN') : cart.heldByName;
    };
    const noteOf = (cart: HeldCartListItem) => parseOrderType(cart.notes).notes;

    const tile = (cart: HeldCartListItem, size: 44 | 48 | 56) => (
        <IconTile size={size} tone={cart.customerName ? 'brandSoft' : 'accentSoft'}>
            {cart.customerName ? <span className="text-[15px] font-black">{initials(cart.customerName)}</span> : <ShoppingBag />}
        </IconTile>
    );

    return (
        <div className="flex h-full min-h-0 flex-1">
            <PageShell
                title={<>{t('heldCarts.title')} {total > 0 && <span className="text-ink-muted">{total}</span>}</>}
                backTo="/settings"
                className="lg:!flex-none lg:w-[440px] lg:border-r lg:border-line-subtle"
            >
                <div className="flex flex-col gap-3 pb-5">
                    {error && <Alert variant="error" message={error} />}
                    {loading && heldCarts.length === 0 && [1, 2].map((i) => <LoadingSkeleton key={i} height={120} />)}
                    {!loading && heldCarts.length === 0 && (
                        <div className="flex flex-col items-center gap-3 py-12 text-center">
                            <span className="flex h-16 w-16 items-center justify-center rounded-full bg-surface-sunken text-primary-300"><CirclePause className="h-8 w-8" /></span>
                            <h3 className="text-lg font-black">{t('heldCarts.empty')}</h3>
                            <p className="text-sm text-ink-muted">{t('heldCarts.emptySubtitle')}</p>
                            <Button variant="primary" onClick={() => navigate('/catalog')}>{t('heldCarts.startShopping')}</Button>
                        </div>
                    )}
                    {heldCarts.map((cart) => {
                        const active = isLandscape && selectedCart?.id === cart.id;
                        const soon = expiringSoon(cart.expiresAt);
                        return (
                            <Card key={cart.id} padding="sm" selected={active} className={clsx('flex flex-col gap-3', isLandscape && 'cursor-pointer')} onClick={isLandscape ? () => select(cart) : undefined}>
                                <div className="flex items-center gap-3">
                                    {tile(cart, 44)}
                                    <span className="flex min-w-0 flex-1 flex-col">
                                        <span className="truncate text-[15px] font-black">{cart.customerName || cart.holdCode}{cart.customerName && noteOf(cart) ? ` · ${noteOf(cart)}` : ''}</span>
                                        <span className="truncate text-xs text-ink-muted">{t('cart.itemsCount', { count: cart.itemCount })} · {typeOrHolder(cart)}</span>
                                        {isLandscape && <span className={clsx('mt-0.5 flex items-center gap-1 text-xs font-bold', soon ? 'text-accent-800' : 'text-ink-muted')}><Clock className="h-[13px] w-[13px]" />{t('heldCarts.expires', { time: timeOf(cart.expiresAt) })}</span>}
                                    </span>
                                    <span className="text-[17px] font-black tnum">{formatCurrency(cart.subtotal)}</span>
                                </div>
                                {!isLandscape && (
                                    <div className="flex items-center gap-2.5 border-t border-line-subtle pt-3">
                                        <span className={clsx('flex flex-1 items-center gap-1 text-xs font-bold', soon ? 'text-accent-800' : 'text-ink-muted')}><Clock className="h-3.5 w-3.5" />{t('heldCarts.expires', { time: timeOf(cart.expiresAt) })}</span>
                                        <button type="button" aria-label={t('heldCarts.cancelCart')} onClick={() => setCancelTarget(cart)} className="flex h-9 w-9 items-center justify-center rounded-full bg-surface-sunken text-ink-secondary hover:text-status-danger"><Trash2 className="h-4 w-4" /></button>
                                        <Button variant="primary" size="sm" onClick={() => handleRetrieveCart(cart)} loading={actionLoading}>{t('heldCarts.retrieve')}</Button>
                                    </div>
                                )}
                            </Card>
                        );
                    })}
                </div>
            </PageShell>

            {isLandscape && (
                <div className="flex min-w-0 flex-1 flex-col gap-4 overflow-y-auto p-[22px_28px]">
                    {selectedCart ? (
                        <>
                            <div className="flex items-center gap-3.5">
                                {tile(selectedCart, 56)}
                                <div className="min-w-0 flex-1">
                                    <h2 className="truncate text-[22px] font-black">{selectedCart.customerName || selectedCart.holdCode}</h2>
                                    <p className="truncate text-[13px] text-ink-muted">{t('heldCarts.heldByAt', { name: selectedCart.heldByName, time: formatDateTime(selectedCart.heldAt) })}{parseOrderType(selectedCart.notes).orderType ? ` · ${typeOrHolder(selectedCart)}` : ''}{noteOf(selectedCart) ? ` · ${noteOf(selectedCart)}` : ''}</p>
                                </div>
                                <span className={clsx('flex h-[34px] items-center gap-1.5 rounded-pill px-3.5 text-[13px] font-extrabold', expiringSoon(selectedCart.expiresAt) ? 'bg-status-warning-soft text-accent-800' : 'bg-surface-sunken text-ink-secondary')}><Clock className="h-[15px] w-[15px]" />{t('heldCarts.expires', { time: timeOf(selectedCart.expiresAt) })}</span>
                            </div>
                            <Card padding="none" className="px-5">
                                {!detail ? <LoadingSkeleton height={120} className="my-4" /> : detail.items.map((item, i) => (
                                    <div key={i} className="flex items-center gap-3.5 border-b border-line-subtle py-4 last:border-b-0">
                                        <IconTile size={48} tone="sunken"><ShoppingBag /></IconTile>
                                        <span className="flex min-w-0 flex-1 flex-col"><span className="truncate text-base font-extrabold">{item.name || `#${item.compositeId}`}</span><span className="text-xs text-ink-muted tnum">{item.quantity} × {formatCurrency(item.unitPrice)}{item.discount > 0 ? ` · −${formatCurrency(item.discount)}` : ''}</span></span>
                                        <span className="text-base font-black tnum">{formatCurrency(item.lineTotal)}</span>
                                    </div>
                                ))}
                            </Card>
                            <div className="px-1"><SummaryLine kind="total" large label={t('heldCarts.subtotal')} value={formatCurrency(detail?.subtotal ?? selectedCart.subtotal)} /></div>
                            <div className="flex-1" />
                            <div className="flex justify-end gap-3">
                                <Button variant="ghost" size="lg" icon={<Trash2 />} onClick={() => setCancelTarget(selectedCart)} className="!text-status-danger">{t('common.delete')}</Button>
                                <Button variant="primary" size="lg" icon={<ShoppingCart />} onClick={() => handleRetrieveCart(selectedCart)} loading={actionLoading}>{t('heldCarts.retrieveToCart')}</Button>
                            </div>
                        </>
                    ) : (
                        <div className="flex flex-1 flex-col items-center justify-center gap-2 text-center text-ink-muted">
                            <CirclePause className="h-10 w-10 text-primary-200" />
                            <p className="text-sm font-bold">{t('heldCarts.selectHint')}</p>
                        </div>
                    )}
                </div>
            )}

            <Sheet
                isOpen={cancelTarget !== null}
                onClose={() => setCancelTarget(null)}
                title={t('heldCarts.cancelHeldCart')}
                size="sm"
                footer={
                    <div className="flex gap-2.5">
                        <Button variant="ghost" size="lg" fullWidth onClick={() => setCancelTarget(null)} disabled={actionLoading}>{t('heldCarts.keepCart')}</Button>
                        <Button variant="danger" size="lg" fullWidth icon={<Trash2 />} onClick={handleCancelCart} loading={actionLoading}>{t('heldCarts.cancelCart')}</Button>
                    </div>
                }
            >
                <p className="text-sm text-ink-secondary">{t('heldCarts.cancelConfirm')}</p>
                {cancelTarget && <p className="mt-3 rounded-md bg-surface-sunken px-3.5 py-2.5 text-sm font-extrabold">{cancelTarget.customerName || cancelTarget.holdCode} · {formatCurrency(cancelTarget.subtotal)}</p>}
            </Sheet>
        </div>
    );
};

export default HeldCartsPage;
